"use client"

import * as React from "react"
import { CircleCheckIcon, InfoIcon, Loader2Icon, OctagonAlertIcon, TriangleAlertIcon } from "lucide-react"
import { Toaster as Sonner, toast, type ToasterProps } from "sonner"

type SharedToasterProps = ToasterProps & { width?: number }

/**
 * Sonner knows only light/dark, so it has to be told which one the app is in.
 * Both applications drive appearance through `data-theme` on the document
 * element - the pre-paint script writes it before first paint and the theme
 * controller updates it - so read that rather than making every consumer wire
 * its own mapping. A caller can still pass `theme` to override.
 */
// Module scope, so all three keep one identity for the life of the module. React
// resubscribes whenever `subscribe` changes, and an inline arrow changes on every render -
// which tears down and rebuilds the MutationObserver each time the Toaster renders.
const subscribeToTheme = (onChange: () => void) => {
  const observer = new MutationObserver(onChange)
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] })
  return () => observer.disconnect()
}
const readDocumentTheme = () =>
  document.documentElement.dataset.theme === "control-room" ? "dark" : "light"
// Server render: the script that sets data-theme has not run, and guessing dark would
// flash. Light matches the design's default preference.
const readServerTheme = () => "light" as const

function useDocumentTheme(): "light" | "dark" {
  return React.useSyncExternalStore(subscribeToTheme, readDocumentTheme, readServerTheme)
}

/**
 * Toast surface for both applications.
 *
 * Two things it does beyond wrapping Sonner:
 *
 * Severity is visible. Sonner styles every toast from one --normal-* triple, so
 * a failure and a confirmation arrive looking identical apart from a small icon.
 * Each type gets the design's own signal colour in its border here, with the
 * fill and the text left neutral - the same "tinted hairline over the surface"
 * the status chips use, rather than a coloured banner.
 *
 * A toast can be dismissed. Sonner's default is to wait it out; every toast
 * here carries a close control instead, which is what makes one dwell time
 * workable for both "Saved" and a validation failure - 5s, overridable per call
 * site, and a message you have not finished reading is yours to keep open.
 */
/** What is left of the screen once Sonner's 16px mobile inset is taken off each
 *  side. An inline style outranks the stylesheet rule that would otherwise size
 *  a toast to a narrow screen, so the clamp has to be restated here. */
const FITS = "calc(100vw - 2rem)"

export function Toaster({ width, style, toastOptions, theme, ...props }: SharedToasterProps) {
  const documentTheme = useDocumentTheme()

  const widthStyle: React.CSSProperties = width
    ? { width: "var(--width)" }
    : { width: "max-content", minWidth: `min(240px, ${FITS})`, maxWidth: `min(380px, ${FITS})` }

  return (
    <Sonner
      className="toaster group"
      theme={theme ?? documentTheme}
      icons={{
        success: <CircleCheckIcon className="size-4" />,
        info: <InfoIcon className="size-4" />,
        warning: <TriangleAlertIcon className="size-4" />,
        // Not an X: every toast carries an X close control, and two read as one broken glyph.
        error: <OctagonAlertIcon className="size-4" />,
        loading: <Loader2Icon className="size-4 animate-spin" />,
      }}
      closeButton
      richColors
      toastOptions={{
        duration: 5000,
        ...toastOptions,
        style: {
          // Sonner's own box is built for a marketing surface: 16px padding, a
          // fixed 356px card, and the close control pinned outside the top-left
          // corner where it reads as debris rather than an affordance. These are
          // operator consoles - dense tables, small type - so the toast is
          // tightened to the density the rest of both apps already use, and the
          // control moves to the trailing edge where a dismiss belongs.
          padding: "11px 14px",
          fontSize: "12.5px",
          gap: "9px",
          // Hug the message: a toast is one line unless it genuinely needs two,
          // rather than Sonner's fixed card with a short message adrift in it.
          // max-content, not auto - an absolutely positioned box shrink-to-fits,
          // so `auto` wraps at the minimum instead of growing to the cap.
          // A caller that asks for an explicit width still gets exactly that.
          ...widthStyle,
          // `auto`, not `unset`: a custom property set to `unset` inherits, so it would
          // fall straight back to Sonner's left-hand default.
          "--toast-close-button-start": "auto",
          "--toast-close-button-end": "0",
          "--toast-close-button-transform": "translate(35%, -35%)",
          // Signal colour in the hairline only - the fill and the text stay on
          // the surface, the way the status chips read. Set here rather than on
          // the toaster because Sonner declares its own rich-colour defaults in
          // a rule on the toast element, which outranks an inherited value.
          "--success-bg": "var(--surface)",
          "--success-text": "var(--content)",
          "--success-border": "color-mix(in srgb, var(--avail-high) 34%, var(--border))",
          "--warning-bg": "var(--surface)",
          "--warning-text": "var(--content)",
          "--warning-border": "color-mix(in srgb, var(--status-warning) 34%, var(--border))",
          // Red at the same alpha reads lighter than the others against the
          // surface, so it carries a few points more.
          "--error-bg": "var(--surface)",
          "--error-text": "var(--content)",
          "--error-border": "color-mix(in srgb, var(--danger) 38%, var(--border))",
          "--info-bg": "var(--surface)",
          "--info-text": "var(--content)",
          "--info-border": "color-mix(in srgb, var(--status-info) 34%, var(--border))",
          ...toastOptions?.style,
        } as React.CSSProperties,
      }}
      style={
        {
          "--normal-bg": "var(--surface)",
          "--normal-text": "var(--content)",
          "--normal-border": "var(--border)",
          "--border-radius": "var(--radius)",
          ...(width ? { "--width": `${width}px` } : {}),
          ...style,
        } as React.CSSProperties
      }
      {...props}
    />
  )
}

/**
 * Sonner's toast entry point, re-exported so no application imports the toast
 * library directly - swapping it stays a change to this file.
 */
export { toast }
